import { useCallback, useState } from 'react';
import { useQuery as useApolloQuery } from '@apollo/client/react';
import { GET_HOT_LEADS } from '../../../lib/graphql/queries/organizations';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';

export interface GQLHotLeadTouchCounts {
  total: number;
  calls: number;
  sms: number;
  emails: number;
}

/**
 * Shared shape of a single last-contact event on a hot-leads row. The
 * backend (SPEC-1879) populates the same six enrichment fields on both
 * inbound (lastFromLead) and outbound (lastFromRemi) so the row can reach
 * Recent-Activity parity. Channel- or direction-specific fields are
 * naturally null for the other direction (e.g. durationSeconds on SMS).
 */
export interface GQLHotLeadLastTouch {
  channel: string | null; // 'sms' | 'email' | 'call'
  direction: string | null; // 'inbound' | 'outbound'
  occurredAt: string | null;
  preview: string | null;
  outcome: string | null;
  sentiment: string | null;
  durationSeconds: number | null;
  callId: string | null;
  messageId: string | null;
  summary: string | null;
}

// Both surfaces use the full last-touch shape — superset of dev's
// channel/direction/occurredAt-only fix (#2411): our enrichment fields
// (outcome, sentiment, duration, callId, messageId, summary) are still
// needed by the Lead Activity row.
export type GQLHotLeadLastFromLead = GQLHotLeadLastTouch;
export type GQLHotLeadLastFromRemi = GQLHotLeadLastTouch;

export interface GQLHotLeadNextTouch {
  channel: string | null;
  scheduledAt: string | null;
  reason: string | null;
}

export interface GQLHotLead {
  contactId: string;
  name: string | null;
  phone: string | null;
  currentScore: number;
  previousScore: number | null;
  touchCounts: GQLHotLeadTouchCounts;
  lastFromLead: GQLHotLeadLastFromLead | null;
  lastFromRemi: GQLHotLeadLastFromRemi | null;
  nextTouch: GQLHotLeadNextTouch | null;
}

interface GetHotLeadsResult {
  organization: {
    hotLeads: {
      items: GQLHotLead[];
      total: number;
      hasMore: boolean;
    };
  };
}

interface UseHotLeadsOptions {
  limit?: number;
  offset?: number;
  scoreMin?: number;
  scoreMax?: number;
  orderBy?: 'score_desc' | 'score_asc';
  search?: string;
  enabled?: boolean;
}

export const useHotLeadsGQL = (options: UseHotLeadsOptions = {}) => {
  const organizationId = useOrganizationStore((s) => s.organization?.id);
  const {
    limit = 20,
    offset = 0,
    scoreMin,
    scoreMax,
    orderBy = 'score_desc',
    search,
    enabled = true,
  } = options;

  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const { data, loading, error, refetch, fetchMore } = useApolloQuery<GetHotLeadsResult>(
    GET_HOT_LEADS,
    {
      variables: { organizationId, limit, offset, scoreMin, scoreMax, orderBy, search },
      skip: !organizationId || !enabled,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: false,
    },
  );

  const leads = data?.organization?.hotLeads?.items ?? [];
  const hasMore = data?.organization?.hotLeads?.hasMore ?? false;

  /**
   * Infinite-scroll load. Same pattern as `useOrgRecentActivitiesGQL`:
   * call `fetchMore` with the next offset, merge new items into the
   * cached list by `contactId` so a row that shifts pages between
   * fetches doesn't render twice.
   */
  const loadMore = useCallback(async () => {
    if (!hasMore || loading || isLoadingMore) return;
    const nextOffset = leads.length;
    setIsLoadingMore(true);
    try {
      await fetchMore({
        variables: { offset: nextOffset },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          const prevItems = prev?.organization?.hotLeads?.items ?? [];
          const nextItems = fetchMoreResult.organization?.hotLeads?.items ?? [];
          const seen = new Set(prevItems.map((l) => l.contactId));
          return {
            organization: {
              ...fetchMoreResult.organization,
              hotLeads: {
                ...fetchMoreResult.organization.hotLeads,
                items: [
                  ...prevItems,
                  ...nextItems.filter((l) => !seen.has(l.contactId)),
                ],
              },
            },
          };
        },
      });
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMore, loading, isLoadingMore, leads.length, fetchMore]);

  return {
    leads,
    total: data?.organization?.hotLeads?.total ?? 0,
    hasMore,
    loading,
    isLoadingMore,
    error,
    refetch,
    loadMore,
  };
};
